import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import axiosInstance from '../../utils/axiosInstance';

const COLORS = {
  bg: 0x020617, 
  grid: 0x22d3ee, 
  earthBase: 0x0f172a, 
  textGlow: '#22d3ee', 
};

export default function Animation3D() {
  const mountRef = useRef(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiTextData, setApiTextData] = useState([]);

  // 1. Fetch Data
  useEffect(() => {
    const fetchTexts = async () => {
      try {
        const { data } = await axiosInstance.get('/animation-texts');
        if (data && data.length > 0) {
            setApiTextData(data);
        } else {
            setApiTextData([
                { text: 'REACT', sub_text: 'LIBRARY' }, 
                { text: 'NODE', sub_text: 'RUNTIME' }
            ]);
        }
      } catch (error) {
        setApiTextData([{ text: 'ERROR', sub_text: '404' }]);
      } finally {
        setDataLoaded(true);
      }
    };
    fetchTexts();
  }, []);

  // 2. Initialize Three.js
  useEffect(() => {
    if (!dataLoaded) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(COLORS.bg);
    scene.fog = new THREE.FogExp2(COLORS.bg, 0.03); 

    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 9; 

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    
    if (mountRef.current) {
      mountRef.current.innerHTML = '';
      mountRef.current.appendChild(renderer.domElement);
    }

    // --- Earth Group ---
    const earthGroup = new THREE.Group();
    scene.add(earthGroup);

    const earthGeometry = new THREE.SphereGeometry(2, 64, 64);
    const earthMaterial = new THREE.MeshPhongMaterial({
      color: COLORS.earthBase,
      emissive: 0x000000,
      specular: COLORS.grid, 
      shininess: 80,
      flatShading: false,
    });
    const earthMesh = new THREE.Mesh(earthGeometry, earthMaterial);
    earthGroup.add(earthMesh);

    const gridGeometry = new THREE.WireframeGeometry(new THREE.IcosahedronGeometry(2.2, 2));
    const gridMaterial = new THREE.LineBasicMaterial({ color: COLORS.grid, transparent: true, opacity: 0.15 });
    const gridMesh = new THREE.LineSegments(gridGeometry, gridMaterial);
    earthGroup.add(gridMesh);

    const ringGeo = new THREE.RingGeometry(3.2, 3.25, 128);
    const ringMat = new THREE.MeshBasicMaterial({ color: COLORS.grid, side: THREE.DoubleSide, transparent: true, opacity: 0.3 });
    const ringMesh = new THREE.Mesh(ringGeo, ringMat);
    ringMesh.rotation.x = Math.PI / 2;
    earthGroup.add(ringMesh);

    const ringGeo2 = new THREE.RingGeometry(2.8, 2.82, 128);
    const ringMat2 = new THREE.MeshBasicMaterial({ color: COLORS.grid, side: THREE.DoubleSide, transparent: true, opacity: 0.1 });
    const ringMesh2 = new THREE.Mesh(ringGeo2, ringMat2);
    ringMesh2.rotation.x = Math.PI / 2;
    earthGroup.add(ringMesh2);

    const ambientLight = new THREE.AmbientLight(0x404040, 0.5); 
    scene.add(ambientLight);
    const sunLight = new THREE.DirectionalLight(0xffffff, 1.5);
    sunLight.position.set(10, 5, 15);
    scene.add(sunLight);
    const rimLight = new THREE.PointLight(COLORS.grid, 3, 50);
    rimLight.position.set(-5, 5, -10);
    scene.add(rimLight);
    const bottomLight = new THREE.PointLight(0x0000ff, 1, 50);
    bottomLight.position.set(0, -10, 5);
    scene.add(bottomLight);

    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 1500;
    const posArray = new Float32Array(particlesCount * 3);
    for (let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 80;
    }
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.03, color: COLORS.grid, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending
    });
    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);

    // --- Floating Text Texture Generation ---
    const createTextTexture = (text, subText) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1024; 
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      
      // Main Text
      ctx.font = 'bold 80px "Courier New", monospace'; 
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = COLORS.textGlow;
      ctx.shadowBlur = 20;
      ctx.fillStyle = 'white';
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);
      
      // Sub Text (The new part)
      if (subText) {
          ctx.font = '24px "Courier New", monospace';
          ctx.shadowBlur = 0;
          ctx.fillStyle = COLORS.textGlow;
          ctx.fillText(`// ${subText.toUpperCase()}`, canvas.width / 2, canvas.height / 2 + 60);
      }

      return new THREE.CanvasTexture(canvas);
    };

    const textMeshes = [];
    
    // Use dynamic data
    apiTextData.forEach((data, index) => {
      // Pass both text and sub_text to the texture generator
      const texture = createTextTexture(data.text, data.sub_text); 
      const mat = new THREE.MeshBasicMaterial({ 
        map: texture, 
        transparent: true, 
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthTest: false 
      });
      const geo = new THREE.PlaneGeometry(6, 1.5);
      const mesh = new THREE.Mesh(geo, mat);

      const angle = (index / apiTextData.length) * Math.PI * 2;
      const radius = 5.5;
      mesh.position.set(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
      
      mesh.userData = {
        radius: radius,
        angle: angle,
        speed: 0.2,
        yOffset: (Math.random() - 0.5) * 3
      };

      scene.add(mesh);
      textMeshes.push(mesh);
    });

    let mouseX = 0;
    let mouseY = 0;
    let targetX = 0;
    let targetY = 0;
    const windowHalfX = window.innerWidth / 2;
    const windowHalfY = window.innerHeight / 2;

    const handleMouseMove = (event) => {
      mouseX = event.clientX - windowHalfX;
      mouseY = event.clientY - windowHalfY;
    };

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    const clock = new THREE.Clock();
    let animationId;

    const animate = () => {
      animationId = requestAnimationFrame(animate);
      const elapsedTime = clock.getElapsedTime();

      earthGroup.rotation.y = elapsedTime * 0.05;
      gridMesh.rotation.y = -elapsedTime * 0.05; 
      gridMesh.rotation.x = Math.sin(elapsedTime * 0.1) * 0.1;
      
      const scale = 1 + Math.sin(elapsedTime * 2) * 0.02;
      ringMesh.scale.set(scale, scale, scale);

      textMeshes.forEach((mesh, index) => {
        const u = mesh.userData;
        const currentAngle = u.angle + elapsedTime * u.speed;
        mesh.position.x = Math.cos(currentAngle) * u.radius;
        mesh.position.z = Math.sin(currentAngle) * u.radius;
        mesh.position.y = u.yOffset + Math.sin(elapsedTime * 1.5 + index) * 0.5;
        mesh.lookAt(camera.position);
      });

      targetX = mouseX * 0.0005;
      targetY = mouseY * 0.0005;
      earthGroup.rotation.x += 0.05 * (targetY - earthGroup.rotation.x);
      earthGroup.rotation.z += 0.05 * (targetX - earthGroup.rotation.z);
      particlesMesh.rotation.y = elapsedTime * 0.01;

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
        mountRef.current.removeChild(renderer.domElement);
      }
      
      earthGeometry.dispose(); earthMaterial.dispose();
      gridGeometry.dispose(); gridMaterial.dispose();
      ringGeo.dispose(); ringMat.dispose();
      ringGeo2.dispose(); ringMat2.dispose();
      particlesGeometry.dispose(); particlesMaterial.dispose();
      textMeshes.forEach(m => { m.geometry.dispose(); m.material.map.dispose(); m.material.dispose(); });
    };
  }, [dataLoaded, apiTextData]); 

  return (
    <div className="relative w-full h-screen bg-[#020617] overflow-hidden font-sans">
      <div className="absolute top-24 left-8 z-10 pointer-events-none select-none">
        <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-[#22d3ee] rounded-full animate-ping"></div>
            <p className="text-[#22d3ee] text-xs font-mono tracking-[0.2em] uppercase">System: Online</p>
        </div>
        <h1 className="text-5xl font-bold tracking-tighter text-white font-sans uppercase mb-1">
            WEB<span className="text-[#22d3ee]">THEME</span>
        </h1>
        <div className="h-[2px] w-24 bg-gradient-to-r from-[#22d3ee] to-transparent mb-4"></div>
        <p className="text-slate-400 text-xs font-mono max-w-xs leading-relaxed">
            // VISUALIZATION_MODE: <span className="text-white">ACTIVE</span><br/>
            BRING YOUR WEBSITE ONLINE. EFFORTLESSLY.
        </p>
      </div>

      <div className="absolute bottom-8 right-8 z-10 pointer-events-none">
          <div className="border-r-2 border-b-2 border-[#22d3ee]/30 p-4 pl-10 pt-10">
             <p className="text-[#22d3ee]/50 text-[10px] font-mono text-right">COORD: 45.234, -12.443</p>
             <p className="text-[#22d3ee]/50 text-[10px] font-mono text-right">FPS: 60</p>
          </div>
      </div>

      <div ref={mountRef} className="w-full h-full block" />
    </div>
  );
}